import { DOCUMENT, InjectionToken, inject } from '@angular/core';

/**
 * Runtime configuration of the module dashboard.
 *
 * Deliberately *not* part of the bundle: which modules an installation offers,
 * and in which order, is an operations decision. Editing
 * `modules.config.json` next to `index.html` and reloading is the whole
 * procedure — no rebuild, no redeploy of the shell.
 *
 * ```json
 * {
 *   "modules": [
 *     { "id": "lohn", "enabled": true, "order": 10, "category": "payroll" }
 *   ]
 * }
 * ```
 *
 * The file names modules; it does not deliver them. An entry only becomes a
 * dashboard card if a package for that id is registered in `module-packages.ts`
 * (see `ModuleRegistryService`).
 */
/**
 * Who an entry is offered to. Deliberately a closed set rather than a role
 * name: this decides whether a module is a product or a piece of workshop
 * equipment, which is not the same question as what a user may do inside one.
 */
export type ModuleAudience = 'everyone' | 'superAdmin';

export interface ModuleConfigEntry {
  /** Matches `SaphirModuleManifest.id` and the key in the package registry. */
  readonly id: string;

  /** Defaults to `true`; set to `false` to hide the module without uninstalling it. */
  readonly enabled: boolean;

  /** Ascending; ties are broken by id so the dashboard order is stable. */
  readonly order: number;

  /** Overrides the manifest's category for this installation. */
  readonly category?: string;

  /**
   * The UMC module `Code` this module's permission verbs are qualified with —
   * the Lohnkern module's id is `lohn` while UMC prefixes its verbs with
   * `payroll`. `CompanyContextService` uses this mapping to mirror the verbs
   * onto the module id (`payroll.read` → `lohn.read`); declare it whenever
   * the code differs from the id.
   *
   * NOT the licensing switch any more: licence gating is keyed by the
   * module's `ModulePublicId` GUID from `environment.umc.moduleIds`, checked
   * against the UMC catalogue (`/service/modules?companyId=…`).
   */
  readonly licenseCode?: string;

  /**
   * Who the module is offered to.
   *
   * `everyone` (the default) is every signed-in user, subject to the usual
   * licence gating. `superAdmin` restricts the card, its sidebar entry, its
   * command-palette entry and its routes to platform staff.
   *
   * For the service modules that ship with the shell — the template new modules
   * are started from, and the worked example — rather than for products. They
   * carry no UMC module GUID, so licence gating says nothing about them
   * (`licenseStateOf` returns `none`) and without this they are offered to
   * every customer alongside the things they actually pay for.
   */
  readonly audience: ModuleAudience;

  /**
   * Reserved for the Native Federation channel: the address a module is served
   * from when it is not installed as an npm package. Parsed and carried through
   * but unused — the runtime loader for it does not exist yet.
   */
  readonly remoteUrl?: string;
}

export interface ModulesConfigParseResult {
  readonly entries: readonly ModuleConfigEntry[];

  /**
   * Human-readable description of everything that was ignored. Whatever is
   * wrong with the file, the rest of it still applies — a typo in one entry
   * must not take the dashboard down.
   */
  readonly problems: readonly string[];
}

/** Location of the config, relative to the application's base href. */
export const MODULES_CONFIG_URL = 'modules.config.json';

export type ModulesConfigLoader = () => Promise<unknown>;

/**
 * Fetches the raw config. Injectable so specs can supply their own without
 * touching the network.
 *
 * Uses `fetch` rather than `HttpClient` on purpose: this is a static asset, and
 * the shell's interceptors rewrite relative URLs onto the API base — they would
 * turn `modules.config.json` into an API call.
 */
export const MODULES_CONFIG_LOADER = new InjectionToken<ModulesConfigLoader>(
  'MODULES_CONFIG_LOADER',
  {
    providedIn: 'root',
    factory: () => {
      const baseUri = inject(DOCUMENT).baseURI;
      return () => fetchModulesConfig(baseUri);
    },
  },
);

async function fetchModulesConfig(baseUri: string): Promise<unknown> {
  // `no-cache` revalidates on every start. A cached copy would defeat the point
  // of runtime configuration: an operator's edit has to take effect on reload.
  const response = await fetch(new URL(MODULES_CONFIG_URL, baseUri), { cache: 'no-cache' });

  if (!response.ok) {
    throw new Error(`${MODULES_CONFIG_URL} responded with HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Turns the parsed JSON into entries, describing rather than throwing on
 * anything it cannot use.
 *
 * The file is edited by hand on a server, so it is expected to be wrong
 * sometimes. A dashboard missing one card is recoverable; an application that
 * refuses to start is not.
 */
export function parseModulesConfig(value: unknown): ModulesConfigParseResult {
  const problems: string[] = [];

  if (!isRecord(value)) {
    return { entries: [], problems: [`${MODULES_CONFIG_URL} is not a JSON object.`] };
  }

  if (!Array.isArray(value['modules'])) {
    return { entries: [], problems: [`${MODULES_CONFIG_URL} has no "modules" array.`] };
  }

  const entries: ModuleConfigEntry[] = [];
  const seen = new Set<string>();

  value['modules'].forEach((raw: unknown, index: number) => {
    const entry = parseEntry(raw, index, problems);

    if (!entry) {
      return;
    }

    if (seen.has(entry.id)) {
      problems.push(`Ignored a duplicate entry for module "${entry.id}"; the first one wins.`);
      return;
    }

    seen.add(entry.id);
    entries.push(entry);
  });

  entries.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));

  return { entries, problems };
}

function parseEntry(
  raw: unknown,
  index: number,
  problems: string[],
): ModuleConfigEntry | undefined {
  if (!isRecord(raw)) {
    problems.push(`Ignored entry #${index}: not an object.`);
    return undefined;
  }

  const id = raw['id'];

  if (typeof id !== 'string' || id.trim().length === 0) {
    problems.push(`Ignored entry #${index}: "id" is missing or empty.`);
    return undefined;
  }

  return {
    id: id.trim(),
    // Absent means enabled: an operator adds an entry to offer a module, not to
    // hide it, so only an explicit `false` disables one.
    enabled: raw['enabled'] !== false,
    // Absent means "keep the order the file is written in".
    order: typeof raw['order'] === 'number' ? raw['order'] : index,
    category: typeof raw['category'] === 'string' ? raw['category'] : undefined,
    licenseCode: typeof raw['licenseCode'] === 'string' ? raw['licenseCode'].trim() : undefined,
    audience: parseAudience(raw['audience'], id.trim(), problems),
    remoteUrl: typeof raw['remoteUrl'] === 'string' ? raw['remoteUrl'] : undefined,
  };
}

/**
 * Reads `audience`, reporting a value it does not recognise.
 *
 * Absent means everyone: an operator restricts a module deliberately. An
 * unrecognised value falls back the same way but says so — the alternative is a
 * misspelt restriction that silently offers workshop equipment to customers,
 * which is precisely what the field exists to prevent.
 */
function parseAudience(raw: unknown, id: string, problems: string[]): ModuleAudience {
  if (raw === undefined || raw === null) {
    return 'everyone';
  }

  if (raw === 'superAdmin' || raw === 'everyone') {
    return raw;
  }

  problems.push(
    `Module "${id}" declares an unknown audience ${JSON.stringify(raw)}; offering it to everyone.`,
  );
  return 'everyone';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
